import type { Language } from '@kk/domain';

import { recordPortalLanguage, portalLanguage } from './activeLanguage';
import { deck } from './deck';
import { ES_1023, ES_310, ES_DRAFT_TEMPLATES, ES_DRAFTS, ES_TEMPLATES } from './es';
import { ES_ROWS } from './esRows';
import { placeholders } from './placeholders';

type Leaf = string | ((...args: never[]) => unknown);

/** Every leaf of a copy tree by the sheet's key form (`a.b`, `list[0].c`), with a way to set it in place. */
function snapshot(root: object): Array<{ key: string; en: Leaf; set: (v: Leaf) => void }> {
  const out: Array<{ key: string; en: Leaf; set: (v: Leaf) => void }> = [];
  const walk = (node: Record<string | number, unknown>, prefix: string) => {
    for (const [k, v] of Object.entries(node)) {
      const key = Array.isArray(node) ? `${prefix}[${k}]` : prefix ? `${prefix}.${k}` : k;
      if (typeof v === 'string' || typeof v === 'function')
        out.push({ key, en: v as Leaf, set: (value) => ((node as Record<string, unknown>)[k] = value) });
      else if (v && typeof v === 'object') walk(v as Record<string, unknown>, key);
    }
  };
  walk(root as Record<string, unknown>, '');
  return out;
}

// the English, taken once before anything can change it
const DECK = snapshot(deck);
const PLACEHOLDERS = snapshot(placeholders);

/** A 23 Oct sheet row: a plain line, or a template whose `{name}`s take the arguments in the order they appear. */
function from1023(full: string): Leaf | undefined {
  const row = ES_1023[full]?.spanish;
  if (row === undefined || !row.includes('{')) return row;
  return ((...args: unknown[]) => {
    let i = 0;
    const seen = new Map<string, unknown>();
    return row.replace(/\{(\w+)\}/g, (_m, name: string) => {
      if (!seen.has(name)) seen.set(name, args[i++]);
      return String(seen.get(name));
    });
  }) as Leaf;
}

/** The Spanish for one deck or placeholders key, or undefined where English stands (no words, or the admin's). */
export function spanishFor(tree: 'deck' | 'placeholders', key: string): Leaf | undefined {
  if (tree === 'deck')
    return (
      ES_TEMPLATES[key] ??
      ES_ROWS[key] ??
      ES_DRAFTS[`deck.${key}`]?.spanish ??
      ES_310[`deck.${key}`] ??
      from1023(`deck.${key}`)
    );
  return (
    ES_DRAFT_TEMPLATES[`placeholders.${key}`] ??
    ES_DRAFTS[`placeholders.${key}`]?.spanish ??
    from1023(`placeholders.${key}`)
  );
}

/**
 * **SWITCH THE CLUBS PORTAL'S WORDS** (Spanish for 3.0.11). The deck and the placeholders are rewritten IN PLACE —
 * every screen imports those objects, so the same references now read the other language — and each key without
 * Spanish keeps its English. The caller redraws the routes afterwards (PortalLanguage keys them on the language).
 * Never called on the admin host: the admin site stays English.
 */
export function setPortalLanguage(language: Language): void {
  if (language === portalLanguage()) return;
  for (const { key, en, set } of DECK) set(language === 'es' ? (spanishFor('deck', key) ?? en) : en);
  for (const { key, en, set } of PLACEHOLDERS) set(language === 'es' ? (spanishFor('placeholders', key) ?? en) : en);
  recordPortalLanguage(language);
  if (typeof document !== 'undefined') document.documentElement.lang = language;
}

/** The English keys, for the tests' coverage checks. */
export const ENGLISH_KEYS = {
  deck: DECK.map((d) => ({ key: d.key, en: d.en })),
  placeholders: PLACEHOLDERS.map((d) => ({ key: d.key, en: d.en })),
};
